import { oidcStorageKeyPrefix, signinCallbackPathnames, silentSigninRequestType } from "./silent-renew-frame.const";

/**
 * This file runs from the entry chunk, before Module Federation has set up the shared scope, so it
 * must not statically import shared packages (React, oidc-client-ts, ...), directly or through
 * another module. Its only static import is the import-free silent-renew-frame.const.ts (enforced
 * by lint). Load anything else with import().
 */

/**
 * True when this document is the hidden iframe oidc-client-ts opens for a silent token renewal:
 * framed, on a sign-in callback path, and carrying a sign-in response (`state`) whose stored
 * request is a silent one. Other OAuth callbacks (such as LinkedIn's) are on other paths.
 *
 * Without this, the iframe boots the whole shell. That copy runs AuthGate and the renew hooks: it
 * takes the cross-tab lock, starts a nested renewal, and on error calls removeUser() and
 * signinRedirect() against storage every tab shares.
 *
 * When the stored state is missing or unreadable, the frame still only hands the response back:
 * the parent owns that request, so the parent reports the failure once and runs the recovery.
 * The app boots only when the stored state belongs to another kind of sign-in (for example a
 * redirect sign-in in a page that embeds the platform on purpose).
 */
export const isSilentRenewFrame = (currentWindow: Window = window): boolean => {
  if (currentWindow.parent === currentWindow) return false;
  if (!signinCallbackPathnames.includes(currentWindow.location.pathname)) return false;

  // Same shape react-oidc-context treats as a sign-in response: a state plus a code or an error.
  const searchParams = new URLSearchParams(currentWindow.location.search);
  const state = searchParams.get("state");
  if (!state || !(searchParams.has("code") || searchParams.has("error"))) return false;

  try {
    const storedState = currentWindow.localStorage.getItem(`${oidcStorageKeyPrefix}${state}`);
    if (storedState === null) return true;

    const { request_type: requestType } = JSON.parse(storedState);
    return requestType === silentSigninRequestType;
  } catch {
    return true;
  }
};

/**
 * Hands the sign-in response in this frame's URL back to the parent tab, which reads the state and
 * exchanges the code. signinSilentCallback() only posts the URL to the parent; it reads no settings
 * and no stored state, so placeholder settings are enough. Automatic renewal is off so this
 * throwaway UserManager starts nothing of its own.
 */
export const completeSilentRenewInParent = async (): Promise<void> => {
  const { UserManager } = await import("oidc-client-ts");

  await new UserManager({
    authority: "",
    client_id: "",
    redirect_uri: "",
    automaticSilentRenew: false,
  }).signinSilentCallback();
};
